var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { EnabledFlow, getOnRampAssets, getOnRampNetworks, OnRampProvider, OnRampPurchaseType, toAssetInfoArray, WalletType, } from '@usecapsule/web-sdk';
import { CenteredText, FilledDisabledInput, Heading, InnerStepContainer, QRContainer, StepContainer } from '../common.js';
import { CpslButton, CpslDivider, CpslIcon, CpslIdenticon, CpslQrCode, CpslSpinner, CpslTab, CpslTabs, CpslText, } from '@usecapsule/react-components';
import { useCapsuleStore, useModalStore, useThemeStore } from '../../stores/index.js';
import { useEffect, useMemo } from 'react';
import { OnRampProviderButton } from '../OnRampComponents/OnRampProviderButton.js';
import { isMobile } from '@usecapsule/web-sdk';
import { useActiveWallet } from '../../hooks/useActiveWallet.js';
import { ModalStep } from '../../utils/steps.js';
import { motion, AnimatePresence } from 'framer-motion';
import { useExternalWallets } from '../../providers/ExternalWalletContext.js';
import { getNetworkFromChainId, getNetworkOrMainNetEquivalent, useCopyToClipboard } from '@usecapsule/react-common';
import { formatNetworkList } from '../../utils/stringFormatters.js';
import styled from 'styled-components';
const TABS = [
    [EnabledFlow.BUY, 'isBuyEnabled', 'creditCard', 'Buy'],
    [EnabledFlow.RECEIVE, 'isReceiveEnabled', 'qrCode', 'Receive'],
    [EnabledFlow.WITHDRAW, 'isWithdrawEnabled', 'arrowCircleBrokenDownLeft', 'Withdraw'],
];
const GENERIC_WALLET = {
    [WalletType.EVM]: 'Ethereum or EVM-based L2s',
    [WalletType.SOLANA]: 'Solana',
    [WalletType.COSMOS]: 'Cosmos',
};
export const AddFunds = () => {
    const [isCopied, copy] = useCopyToClipboard();
    const capsule = useCapsuleStore(state => state.capsule);
    const appName = useThemeStore(state => state.appName);
    const onRampConfig = useModalStore(state => state.onRampConfig);
    const storedTab = useModalStore(state => state.accountAddFundTab);
    const setTab = useModalStore(state => state.setAccountAddFundTab);
    const setStep = useModalStore(state => state.setStep);
    const setOnRampPurchase = useModalStore(state => state.setOnRampPurchase);
    const { chainId } = useExternalWallets();
    const activeWallet = useActiveWallet();
    const tabs = TABS.filter(([, key]) => !!onRampConfig[key]);
    const isMultiFlow = tabs.length > 1;
    const tab = storedTab !== null && storedTab !== void 0 ? storedTab : tabs[0][0];
    const address = useMemo(() => (activeWallet ? capsule.getDisplayAddress(activeWallet.id, { addressType: activeWallet.type }) : ''), [capsule, activeWallet === null || activeWallet === void 0 ? void 0 : activeWallet.id, activeWallet === null || activeWallet === void 0 ? void 0 : activeWallet.type]);
    const onSetTab = (event) => {
        setTab(event.detail.tab);
    };
    const onCopy = () => {
        copy(address);
    };
    const [allowedNetworks, allowedAssets, isProviderAllowed] = useMemo(() => {
        if (!onRampConfig || !activeWallet) {
            return [[], [], {}];
        }
        const action = tab === EnabledFlow.BUY ? OnRampPurchaseType.BUY : OnRampPurchaseType.SELL;
        const detectedNetwork = getNetworkFromChainId(chainId);
        const isExternal = activeWallet.isExternal && !!detectedNetwork;
        const allowedNetworks = isExternal
            ? [getNetworkOrMainNetEquivalent(detectedNetwork, onRampConfig.testMode)]
            : getOnRampNetworks(onRampConfig.assetInfo, {
                walletType: activeWallet.type,
                allowed: onRampConfig.allowedAssets ? Object.keys(onRampConfig.allowedAssets) : undefined,
            });
        const allowedAssetsLookup = allowedNetworks.reduce((acc, network) => {
            var _a;
            const configValue = (_a = onRampConfig.allowedAssets) === null || _a === void 0 ? void 0 : _a[network];
            const allowed = configValue === true ? undefined : configValue;
            return Object.assign(Object.assign({}, acc), { [network]: getOnRampAssets(onRampConfig.assetInfo, { walletType: activeWallet.type, network, allowed }) });
        }, {});
        const isProviderAllowed = onRampConfig.providers.reduce((acc, id) => {
            const hasMatch = toAssetInfoArray(onRampConfig.assetInfo).some(([type, network, asset, validProviders]) => {
                var _a, _b;
                return (type === activeWallet.type &&
                    allowedNetworks.includes(network) &&
                    (!allowedAssetsLookup[network] || allowedAssetsLookup[network].includes(asset)) &&
                    !!((_b = (_a = validProviders[id]) === null || _a === void 0 ? void 0 : _a[1]) === null || _b === void 0 ? void 0 : _b[action]));
            });
            return Object.assign(Object.assign({}, acc), { [id]: hasMatch });
        }, {});
        return [allowedNetworks, [...new Set(Object.values(allowedAssetsLookup).flat())], isProviderAllowed];
    }, [activeWallet === null || activeWallet === void 0 ? void 0 : activeWallet.type, activeWallet === null || activeWallet === void 0 ? void 0 : activeWallet.isExternal, tab, onRampConfig.assetInfo, onRampConfig.allowedAssets, chainId]);
    useEffect(() => {
        setOnRampPurchase(undefined);
    }, []);
    useEffect(() => {
        setOnRampPurchase(undefined);
    }, []);
    if (!onRampConfig || !activeWallet) {
        return (_jsx(SpinnerContainer, { children: _jsx(CpslSpinner, {}) }));
    }
    useEffect(() => {
        setOnRampPurchase(undefined);
    }, []);
    return (_jsxs(StepContainer, { children: [isMultiFlow && (_jsx(InnerStepContainer, { children: _jsx(CpslTabs, { selectedTab: tab, onCpslTabsChanged: onSetTab, children: TABS.map(([tab, _, icon, title]) => (_jsxs(CpslTab, { tab: tab, children: [_jsx(CpslIcon, { slot: "start", icon: icon }), title] }, tab))) }) })), _jsx(_Fragment, { children: [EnabledFlow.BUY, EnabledFlow.WITHDRAW].includes(tab) ? (_jsxs(_Fragment, { children: [_jsx(Heading, { variant: "headingS", weight: "bold", children: "Choose Provider" }), _jsxs($InnerStepContainer, { children: [_jsx(NoProviders, { isHidden: Object.values(isProviderAllowed).some(v => !!v), variant: "bodyM", children: "No providers are available for this wallet" }), _jsx(AnimatePresence, { children: onRampConfig.providers.map((id, index) => {
                                        return isProviderAllowed[id] ? (_jsx(motion.div, { style: { width: '100%' }, layout: true, initial: { opacity: 0, transform: 'translateX(25px)' }, animate: { opacity: 1, transform: 'none' }, exit: { opacity: 0, transform: 'translateX(-25px)' }, transition: { duration: 0.2 }, children: _jsx(OnRampProviderButton, { config: onRampConfig, index: index, onClick: () => __awaiter(void 0, void 0, void 0, function* () {
                                                    var _a;
                                                    if (!(activeWallet === null || activeWallet === void 0 ? void 0 : activeWallet.type))
                                                        return;
                                                    const isPopup = id === OnRampProvider.MOONPAY;
                                                    const { onRampPurchase: newOnRampPurchase } = yield capsule.initiateOnRampTransaction({
                                                        walletId: activeWallet.isExternal ? undefined : activeWallet.id,
                                                        externalWalletAddress: activeWallet.isExternal ? activeWallet.id : undefined,
                                                        shouldOpenPopup: isPopup,
                                                        params: {
                                                            type: tab === EnabledFlow.BUY ? OnRampPurchaseType.BUY : OnRampPurchaseType.SELL,
                                                            walletType: activeWallet.type,
                                                            provider: id,
                                                            networks: allowedNetworks,
                                                            assets: allowedAssets,
                                                            defaultNetwork: onRampConfig.defaultOnRampNetwork,
                                                            defaultAsset: onRampConfig.defaultOnRampAsset,
                                                            fiatQuantity: (_a = onRampConfig.defaultBuyAmount) === null || _a === void 0 ? void 0 : _a[0],
                                                            testMode: onRampConfig.testMode,
                                                        },
                                                    });
                                                    setOnRampPurchase(Object.assign(Object.assign({}, newOnRampPurchase), { fiat: 'USD' }));
                                                    !isPopup && setStep(ModalStep.ADD_FUNDS_AWAITING);
                                                }) }, id) }, id)) : null;
                                    }) })] })] })) : (_jsxs(_Fragment, { children: [_jsx(InnerStepContainer, { children: _jsxs(FilledDisabledInput, { noAutoDisable: true, readonly: true, placeholder: address, children: [_jsx(CpslIdenticon, { slot: "start", size: "32px", hash: capsule.getIdenticonHash(activeWallet.id, activeWallet.type) }), _jsx(CpslButton, { slot: "end", variant: "ghost", onClick: onCopy, children: _jsx(CpslIcon, { icon: isCopied ? 'check' : 'copy' }) })] }, address) }), !isMobile() && (_jsxs(_Fragment, { children: [_jsx(CpslDivider, { children: "or" }), _jsxs(InnerStepContainer, { children: [_jsx(QRContainer, { children: !address ? _jsx(CpslSpinner, { size: 100 }) : _jsx(CpslQrCode, { url: address }, address) }), _jsx(CpslText, { weight: "semiBold", color: "secondary", children: "Scan with your crypto wallet" })] })] })), _jsxs(InnerStepContainer, { children: [_jsxs(CenteredText, { weight: "semiBold", children: [!!onRampConfig.allowedAssets && allowedNetworks.length > 0 ? (appName !== null && appName !== void 0 ? appName : 'This App') : 'This Wallet', " Only Supports:"] }), _jsx(CenteredText, { weight: "medium", color: "secondary", children: !!onRampConfig.allowedAssets && allowedNetworks.length > 0
                                        ? formatNetworkList(allowedNetworks)
                                        : GENERIC_WALLET[activeWallet.type] })] })] })) })] }));
};
const SpinnerContainer = styled(StepContainer) `
  margin: 50% 0;
`;
const $InnerStepContainer = styled(InnerStepContainer) `
  position: relative;
  min-height: 270px;
`;
const NoProviders = styled(CpslText) `
  width: 100%;
  text-align: center;
  visibility: ${({ isHidden }) => (isHidden ? 'hidden' : 'visible')};
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  transition: visibility 0.2s;
`;
